/**
 * fastenerSelection.ts - Moduł doboru mocowań i łączeń
 * PlexiSystem Engineering Module
 */

import { MaterialKey } from '../constants/materials';
import { 
  FASTENERS,
  calculateScrewCount,
  calculateBondArea,
  selectHinges,
  calculateScrewSpacing,
  checkThreadingPossibility
} from '../constants/fasteners';

export interface FastenerInput {
  mass_kg: number;
  dynamic: boolean;
  open_close_cycles?: number;
  material: MaterialKey;
  thickness_mm: number;
  joint_type: 'fixed' | 'hinge' | 'removable' | 'sliding';
  load_direction?: 'axial' | 'shear' | 'combined';
  adhesive_option?: boolean;
}

export interface FastenerResult {
  primary_method: {
    type: string;
    details: any;
    capacity_N: number;
  };
  alternative_methods?: Array<{
    type: string;
    details: any;
    pros: string[];
    cons: string[];
  }>;
  bom: Array<{
    item: string;
    quantity: number;
    specification: string;
    notes?: string;
  }>;
  assembly_notes: string[];
  torque_specs?: Record<string, number>;
  warnings: string[];
}

/**
 * Główna funkcja doboru mocowań
 */
export function calcFasteners(input: FastenerInput): FastenerResult {
  const result: FastenerResult = {
    primary_method: {
      type: '',
      details: {},
      capacity_N: 0
    },
    bom: [],
    assembly_notes: [],
    warnings: []
  };
  
  // Oblicz siłę referencyjną
  const gravity = 9.81;
  const dynamicFactor = input.dynamic ? 1.5 : 1.0;
  const cycleFactor = input.open_close_cycles && input.open_close_cycles > 10000 ? 1.2 : 1.0;
  const F_ref = input.mass_kg * gravity * dynamicFactor * cycleFactor;
  
  // Określ typ obciążenia
  const loadType = input.dynamic ? 'dynamic' : 'static';
  const loadDirection = input.load_direction || 'combined';
  
  // Dobór metody w zależności od typu połączenia
  switch (input.joint_type) {
    case 'fixed':
      result.primary_method = selectFixedJoint(F_ref, input.thickness_mm, loadType, loadDirection);
      break;
      
    case 'hinge':
      result.primary_method = selectHingeJoint(input.mass_kg * cycleFactor, input.thickness_mm);
      break;
      
    case 'removable':
      result.primary_method = selectRemovableJoint(F_ref, input.thickness_mm);
      break;
      
    case 'sliding':
      result.primary_method = selectSlidingJoint(F_ref, input.thickness_mm);
      break;
  }
  
  // Generuj BOM
  result.bom = generateBOM(result.primary_method, input.material);
  
  // Dodaj alternatywne metody
  result.alternative_methods = getAlternativeMethods(input);
  
  // Noty montażowe
  result.assembly_notes = generateAssemblyNotes(result.primary_method, input);
  
  // Momenty dokręcania
  if (result.primary_method.type.includes('screw')) {
    result.torque_specs = generateTorqueSpecs(result.primary_method);
  }
  
  // Ostrzeżenia
  result.warnings = generateWarnings(input, result.primary_method);
  
  return result;
}

/**
 * Dobór połączenia stałego
 */
function selectFixedJoint(
  load_N: number,
  thickness_mm: number,
  loadType: string,
  loadDirection: string
): FastenerResult['primary_method'] {
  // Próbuj różne rozmiary śrub
  const screwSizes: Array<'M3' | 'M4' | 'M5' | 'M6' | 'M8' | 'M10'> = ['M3', 'M4', 'M5', 'M6', 'M8', 'M10'];
  
  for (const size of screwSizes) {
    const threading = checkThreadingPossibility(thickness_mm, size);
    if (!threading.possible) continue;
    
    const screwCalc = calculateScrewCount(
      load_N,
      size,
      loadType as any,
      loadDirection as any
    );
    
    if (screwCalc.count <= 6) { // Rozsądna liczba śrub
      const spacing = calculateScrewSpacing(parseInt(size.slice(1)));
      
      return {
        type: `screw_${size}`,
        details: {
          size,
          count: screwCalc.count,
          pattern: screwCalc.count <= 2 ? 'linear' : 'rectangular',
          spacing_mm: spacing.optimal_mm,
          edge_distance_mm: spacing.edge_min_mm,
          safety_factor: screwCalc.actualSF,
          threading_note: threading.recommendation
        },
        capacity_N: screwCalc.screwCapacity * screwCalc.count
      };
    }
  }
  
  // Jeśli śruby nie wystarczą, zaproponuj klejenie
  const bondCalc = calculateBondArea(load_N, 'acrylic_cement', loadType as any);
  
  return {
    type: 'adhesive_bond',
    details: {
      adhesive_type: 'acrylic_cement',
      area_required_cm2: bondCalc.area_cm2,
      joint_width_mm: bondCalc.width_for_10mm_joint,
      cure_time_h: 24,
      strength_MPa: 30
    },
    capacity_N: load_N * 2 // Z współczynnikiem bezpieczeństwa
  };
}

/**
 * Dobór zawiasów
 */
function selectHingeJoint(
  doorWeight_kg: number,
  doorHeight_mm: number
): FastenerResult['primary_method'] {
  const hingeSelection = selectHinges(doorWeight_kg, doorHeight_mm);
  
  return {
    type: `hinge_${hingeSelection.type}`,
    details: {
      ...hingeSelection,
      mounting_screws: hingeSelection.type === 'piano' ? 'M3 co 100mm' : `M4 × 4 per hinge`
    },
    capacity_N: hingeSelection.capacity_kg * 9.81
  };
}

/**
 * Dobór połączenia rozłącznego
 */
function selectRemovableJoint(
  load_N: number,
  thickness_mm: number
): FastenerResult['primary_method'] {
  return {
    type: 'threaded_insert',
    details: {
      insert_type: 'brass_ultrasonic',
      thread: 'M5',
      count: Math.ceil(load_N / 500), // 500N na wkładkę
      installation: 'Ultrasonic welding',
      screw_spec: 'M5×16 DIN 912'
    },
    capacity_N: Math.ceil(load_N / 500) * 500
  };
}

/**
 * Dobór połączenia przesuwnego
 */
function selectSlidingJoint(
  load_N: number,
  thickness_mm: number
): FastenerResult['primary_method'] {
  return {
    type: 'slide_track',
    details: {
      track_type: 'T-slot',
      width_mm: 10,
      slider: 'PTFE bushing',
      retention: 'Spring clip',
      max_travel_mm: 500
    },
    capacity_N: 1000 // Typowa nośność prowadnicy
  };
}

/**
 * Generuje BOM
 */
function generateBOM(
  method: FastenerResult['primary_method'],
  material: MaterialKey
): FastenerResult['bom'] {
  const bom: FastenerResult['bom'] = [];
  
  if (method.type.startsWith('screw_')) {
    const size = method.details.size;
    const count = method.details.count;
    
    bom.push({
      item: `Śruba ${size}×20`,
      quantity: count,
      specification: `DIN 912, stal nierdzewna A2`,
      notes: 'Łeb walcowy z gniazdem sześciokątnym'
    });
    
    bom.push({
      item: `Podkładka ${size}`,
      quantity: count * 2,
      specification: `DIN 125, Ø zew. ${parseInt(size.slice(1)) * 3}mm`,
      notes: 'Duża średnica dla tworzyw'
    });
    
    if (method.details.threading_note?.includes('wkładka')) {
      bom.push({
        item: `Wkładka gwintowana ${size}`,
        quantity: count,
        specification: `Mosiądz, ultradźwiękowa`,
        notes: 'Do wtapiania ultradźwiękowego'
      });
    }
  }
  
  if (method.type.startsWith('hinge_')) {
    if (method.details.type === 'piano') {
      bom.push({
        item: 'Zawias pianinowy',
        quantity: 1,
        specification: `L=${method.details.size}, stal nierdzewna`,
        notes: 'Pełna długość drzwi'
      });
      
      bom.push({
        item: 'Śruba M3×10',
        quantity: Math.ceil(parseInt(method.details.size) / 100) * 2,
        specification: 'DIN 7991, łeb stożkowy',
        notes: 'Co 100mm, obustronne'
      });
    } else {
      bom.push({
        item: `Zawias ${method.details.size}`,
        quantity: method.details.count,
        specification: 'Stal nierdzewna, łożyskowany',
        notes: `Nośność ${method.details.capacity_kg}kg/komplet`
      });
    }
  }
  
  if (method.type === 'adhesive_bond') {
    const area_cm2 = method.details.area_required_cm2;
    const volume_ml = area_cm2 * 0.1; // 1mm grubość spoiny
    
    bom.push({
      item: 'Klej akrylowy',
      quantity: Math.ceil(volume_ml / 50), // Tubki 50ml
      specification: 'Acrifix 192, dwuskładnikowy',
      notes: `Powierzchnia klejenia: ${area_cm2}cm²`
    });
    
    bom.push({
      item: 'Primer',
      quantity: 1,
      specification: `Do ${material}`,
      notes: 'Poprawa adhezji'
    });
  }
  
  return bom;
}

/**
 * Generuje alternatywne metody
 */
function getAlternativeMethods(input: FastenerInput): FastenerResult['alternative_methods'] {
  const alternatives: FastenerResult['alternative_methods'] = [];
  
  // Klejenie UV jako alternatywa
  if (input.material === 'PMMA' || input.material === 'PC') {
    alternatives.push({
      type: 'UV bonding',
      details: {
        adhesive: 'UV-curing acrylic',
        cure_time: '60 seconds',
        equipment: 'UV lamp 365nm'
      },
      pros: [
        'Niewidoczna spoina',
        'Szybkie utwardzanie',
        'Estetyczne połączenie'
      ],
      cons: [
        'Tylko dla materiałów przezroczystych',
        'Wymaga sprzętu UV',
        'Ograniczona wytrzymałość'
      ]
    });
  }
  
  // Spawanie ultradźwiękowe
  if (['ABS', 'PC', 'PMMA'].includes(input.material)) {
    alternatives.push({
      type: 'Ultrasonic welding',
      details: {
        frequency: '20 kHz',
        amplitude: '30-60 μm',
        weld_time: '0.5-2s'
      },
      pros: [
        'Bardzo mocne połączenie',
        'Szybki proces',
        'Bez dodatków'
      ],
      cons: [
        'Wymaga specjalnego sprzętu',
        'Tylko dla kompatybilnych tworzyw',
        'Połączenie nierozłączne'
      ]
    });
  }
  
  // Zatrzaski
  if (input.joint_type === 'removable') {
    alternatives.push({
      type: 'Snap-fit',
      details: {
        type: 'Cantilever snap',
        undercut: '1.5mm',
        draft_angle: '1°'
      },
      pros: [
        'Szybki montaż',
        'Bez dodatkowych elementów',
        'Wielokrotny montaż/demontaż'
      ],
      cons: [
        'Wymaga precyzyjnej obróbki',
        'Możliwe pęknięcia',
        'Ograniczona nośność'
      ]
    });
  }
  
  return alternatives;
}

/**
 * Generuje noty montażowe
 */
function generateAssemblyNotes(
  method: FastenerResult['primary_method'],
  input: FastenerInput
): string[] {
  const notes: string[] = [];
  
  // Ogólne
  notes.push(`Temperatura montażu: 18-25°C`);
  notes.push(`Wilgotność: 40-60%`);
  
  // Specyficzne dla metody
  if (method.type.startsWith('screw_')) {
    notes.push(`Wywierć otwory pilotujące Ø${parseInt(method.details.size.slice(1)) * 0.8}mm`);
    notes.push(`Użyj podkładek o dużej średnicy po obu stronach`);
    notes.push(`Dokręcaj stopniowo, na krzyż`);
    notes.push(`NIE dokręcaj zbyt mocno - ryzyko pęknięcia`);
  }
  
  if (method.type === 'adhesive_bond') {
    notes.push(`Oczyść powierzchnie acetonem lub IPA`);
    notes.push(`Nałóż primer jeśli wymagany`);
    notes.push(`Zapewnij docisk 0.1-0.3 MPa podczas utwardzania`);
    notes.push(`Pełna wytrzymałość po ${method.details.cure_time_h}h`);
  }
  
  if (method.type.startsWith('hinge_')) {
    notes.push(`Sprawdź współosiowość zawiasów`);
    notes.push(`Zastosuj smar silikonowy na sworzniach`);
    notes.push(`Pozostaw luz 2mm na rozszerzalność`);
  }
  
  return notes;
}

/**
 * Generuje specyfikację momentów
 */
function generateTorqueSpecs(method: FastenerResult['primary_method']): Record<string, number> {
  const specs: Record<string, number> = {};
  
  if (method.details.size) {
    const size = method.details.size as keyof typeof FASTENERS.torque_Nm;
    specs[size] = FASTENERS.torque_Nm[size];
    specs[`${size}_plastic`] = FASTENERS.torque_Nm[size] * 0.7; // 70% dla tworzyw
  }
  
  return specs;
}

/**
 * Generuje ostrzeżenia
 */
function generateWarnings(
  input: FastenerInput,
  method: FastenerResult['primary_method']
): string[] {
  const warnings: string[] = [];
  
  // Ostrzeżenia materiałowe
  if (input.material === 'PMMA' && method.type.startsWith('screw_')) {
    warnings.push('PMMA jest kruche - zachowaj ostrożność przy dokręcaniu');
  }
  
  if (input.thickness_mm < 3 && method.type.startsWith('screw_')) {
    warnings.push('Cienki materiał - rozważ użycie nakrętek zamiast gwintowania');
  }
  
  // Ostrzeżenia dynamiczne
  if (input.dynamic) {
    warnings.push('Obciążenia dynamiczne - sprawdzaj regularnie luz połączeń');
  }
  
  if (input.open_close_cycles && input.open_close_cycles > 50000) {
    warnings.push('Duża liczba cykli - rozważ łożyskowane zawiasy');
  }
  
  // Ostrzeżenia temperaturowe
  warnings.push('Uwzględnij rozszerzalność termiczną - pozostaw luzy montażowe');
  
  return warnings;
}
